import { useState, useEffect, useCallback } from 'react';
import { Phone, MapPin, ChevronDown, ChevronUp, CalendarClock } from 'lucide-react';
import { OwnerLayout } from '../../components/OwnerLayout';
import api from '../../api';

function getInitials(name) {
  return name?.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2) || 'M';
}

function formatDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }); }
  catch { return d; }
}

function formatDateTime(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  } catch { return d; }
}

function daysOverdue(d) {
  if (!d) return 0;
  const diff = new Date(new Date().toDateString()) - new Date(d);
  return Math.max(0, Math.floor(diff / 86400000));
}

function openMaps(lat, lng) {
  if (lat && lng) window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
}

export default function OwnerFollowups() {
  const [followups, setFollowups] = useState({ overdue: [], today: [], thisWeek: [] });
  const [agents, setAgents] = useState([]);
  const [activeTab, setActiveTab] = useState('overdue');
  const [agentFilter, setAgentFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const params = agentFilter ? `?agentId=${agentFilter}` : '';
      const [fuRes, agentsRes] = await Promise.all([
        api.get(`/followups${params}`),
        api.get('/agents')
      ]);
      setFollowups(fuRes.data);
      setAgents(agentsRes.data || []);
      if (fuRes.data.overdue?.length === 0 && fuRes.data.today?.length > 0) {
        setActiveTab('today');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [agentFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-refresh on visibility change
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') loadData();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [loadData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const tabs = [
    { key: 'overdue', label: 'Overdue', count: followups.overdue?.length || 0 },
    { key: 'today', label: 'Today', count: followups.today?.length || 0 },
    { key: 'thisWeek', label: 'This Week', count: followups.thisWeek?.length || 0 },
  ];

  const items = followups[activeTab] || [];

  return (
    <OwnerLayout title="Follow-ups" overdueCount={followups.overdue?.length || 0}>
      {/* Summary Cards */}
      <div className="od-stats" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: '16px' }}>
        {tabs.map(tab => (
          <div
            key={tab.key}
            className={`od-stat clickable ${activeTab === tab.key ? 'active-tab' : ''}`}
            onClick={() => setActiveTab(tab.key)}
            style={activeTab === tab.key ? { borderColor: 'var(--navy)', borderWidth: '2px' } : {}}
            data-testid={`followup-card-${tab.key}`}
          >
            <div className="od-stat-lbl">{tab.label}</div>
            <div className={`od-stat-val ${tab.key === 'overdue' && tab.count > 0 ? 'red' : ''}`}>{tab.count}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <select
          className="form-select"
          value={agentFilter}
          onChange={e => setAgentFilter(e.target.value)}
          style={{ width: 'auto', minWidth: '160px' }}
          data-testid="agent-filter"
        >
          <option value="">All Agents</option>
          {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <span style={{ fontSize: '13px', color: 'var(--g3)' }}>
          {items.length} follow-up{items.length !== 1 ? 's' : ''} {activeTab === 'overdue' ? 'overdue' : activeTab === 'today' ? 'due today' : 'this week'}
        </span>
      </div>

      {/* Follow-ups List */}
      <div className="panel">
        <div className="fu-tabs">
          {tabs.map(tab => (
            <div
              key={tab.key}
              className={`fu-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <span className="num">{tab.count}</span>
              <span className="lbl">{tab.label}</span>
            </div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: '32px', textAlign: 'center', color: 'var(--g3)' }}>Loading...</div>
        ) : items.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>
              {activeTab === 'overdue' ? '✅' : '📅'}
            </div>
            <div style={{ fontFamily: 'var(--fh)', fontWeight: 700, color: 'var(--navy)' }}>
              {activeTab === 'overdue' ? 'No overdue follow-ups!' : activeTab === 'today' ? 'No follow-ups due today' : 'No follow-ups this week'}
            </div>
          </div>
        ) : (
          <div>
            {items.map(m => {
              const isExpanded = expandedId === m.id;
              return (
                <div key={m.id} className="od-fu-card" data-testid={`followup-${m.id}`}>
                  {/* Clickable Row */}
                  <div className="od-fu-row" onClick={() => setExpandedId(isExpanded ? null : m.id)}>
                    <div className="od-fu-av">{getInitials(m.businessName)}</div>
                    <div className="od-fu-info">
                      <div className="od-fu-name">{m.businessName}</div>
                      <div className="od-fu-meta">
                        <a href={`tel:${m.phone}`} className="od-fu-phone" onClick={e => e.stopPropagation()}>
                          <Phone size={10} /> {m.phone}
                        </a>
                        <span className="od-fu-agent">{m.agentName || 'Agent'}</span>
                      </div>
                    </div>
                    <div className="od-fu-right">
                      {m.nextVisitDate && (
                        <div className="od-fu-date"><CalendarClock size={10} /> {formatDate(m.nextVisitDate)}</div>
                      )}
                      {activeTab === 'overdue' ? (
                        <span className="badge-overdue">{daysOverdue(m.nextVisitDate)}d overdue</span>
                      ) : (
                        <span className={`badge-${(m.status || 'new').toLowerCase()}`}>{m.status}</span>
                      )}
                      {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="od-fu-detail">
                      <div className="od-fu-detail-row">
                        <span className="od-fu-dl">Owner:</span> {m.ownerName || '—'}
                      </div>
                      {m.address && (
                        <div className="od-fu-detail-row">
                          <span className="od-fu-dl">Address:</span> {m.address}{m.city ? `, ${m.city}` : ''}
                        </div>
                      )}
                      {m.nextVisitDate && (
                        <div className="od-fu-detail-row">
                          <span className="od-fu-dl">Next Visit:</span> {formatDate(m.nextVisitDate)}
                          {activeTab === 'overdue' && <span style={{ color: 'var(--red)', fontWeight: 600, marginLeft: '6px' }}>({daysOverdue(m.nextVisitDate)} days overdue)</span>}
                        </div>
                      )}
                      {m.gps_lat && (
                        <div className="od-fu-detail-row">
                          <button className="od-fu-gps" onClick={() => openMaps(m.gps_lat, m.gps_lng)}>
                            <MapPin size={11} /> View on Map
                          </button>
                        </div>
                      )}
                      {(m.services || []).length > 0 && (
                        <div className="od-fu-detail-row">
                          <span className="od-fu-dl">Services:</span>
                          <div className="od-fu-tags">
                            {m.services.map(s => <span key={s.id} className={`stag ${s.slug === 'upi_qr' ? 'upi' : ''}`}>{s.name}</span>)}
                          </div>
                        </div>
                      )}

                      {/* Agent Visit Notes */}
                      {m.visitLog && m.visitLog.length > 0 ? (
                        <div className="od-fu-notes">
                          <div className="od-fu-notes-title">Agent Notes ({m.visitLog.length})</div>
                          {m.visitLog.slice(-5).reverse().map((v, i) => (
                            <div key={i} className="od-fu-note">
                              <div className="od-fu-note-date">{formatDateTime(v.visitedAt || v.addedAt)}</div>
                              <div className="od-fu-note-text">{v.note}</div>
                              {(v.addedByName || v.createdByName) && <div className="od-fu-note-by">— {v.addedByName || v.createdByName}</div>}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="od-fu-notes">
                          <div className="od-fu-notes-title">Agent Notes</div>
                          <div style={{ fontSize: '12px', color: 'var(--g3)', padding: '6px 0' }}>No notes recorded yet</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </OwnerLayout>
  );
}
